<template>
  <div>
    <div>
      <h4 class="mb-8 mt-4 text-left text-dark-gray sm:mt-2">
        {{
          firstTime
            ? 'Review the certifications we were able to independently verify.'
            : 'Review your carrier certifications.'
        }}
      </h4>

      <div>
        <div class="flex flex-col gap-6">
          <div class="flex-col text-left text-dark-gray">
            <label>CERTIFIED</label>
            <ul
              v-if="certified?.length > 0"
              class="flex flex-row flex-wrap gap-3"
            >
              <li
                v-for="certification in certified"
                :key="certification.id"
                class="text-sm text-dark-gray"
              >
                <div v-if="certified">
                  <div class="flex gap-1">
                    {{
                      certification.is_type == 'PHMSA'
                        ? certification.is_type + ' (Hazmat)'
                        : certification.is_type
                    }}
                    <img
                      src="@/assets/certified.svg"
                      alt="certified icon"
                    />
                  </div>
                  <p
                    v-if="!firstTime"
                    class="cert-id text-body-color"
                  >
                    {{ certification.id }}
                  </p>
                </div>
              </li>
            </ul>
            <p v-else>None</p>
          </div>
          <div
            v-if="uncertified"
            class="text-left"
          >
            <label class="text-gray-over-dark">UNCERTIFIED</label>
            <p class="text-gray-over-dark">
              {{ uncertifiedString }}
            </p>
          </div>
          <div
            v-if="pendingCertifications?.length > 0"
            class="text-left"
          >
            <label class="text-gray-over-dark">PENDING VERIFICATION</label>
            <p class="text-gray-over-dark">
              {{ pendingCertificationString }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      :is-button-disabled="isButtonDisabled"
      @next="handleNext"
      @back="handleBack"
      @edit="handleEdit"
      @exit-modal="emitExitModal"
    />
  </div>
</template>

<script>
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import { useFormRouter } from '@/composables/formRouter';
import { useStore } from 'vuex';
import { computed } from 'vue';
import stepInformation from '@/common/stepInformation';

export default {
  name: 'CertificationsStepOne',
  components: {
    FormNavigationButtons,
  },
  props: {
    data: Object,
    updateField: Function,
    nextStep: Function,
    formPosition: Number,
    title: String,
    stepName: String,
    skipStep: Function,
    screenSize: String,
  },
  setup(_, { emit }) {
    const store = useStore();
    const componentIdentifier = 'Certifications-1';
    const formRouter = useFormRouter(componentIdentifier);
    const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
    const firstTime = computed(
      () =>
        store.state.firstTime?.[
          formRouter.componentStep.value?.firstTimeIdentifier
        ]
    );
    const buttonsToDisplay = computed(() => {
      if (firstTime.value) return 'report-error';
      return 'continue-edit';
    });
    const isButtonDisabled = false;

    // nextChecks: Checks to be reviewed when the user clicks the Continue button (but before any store.state.formRoutes.activeStep changes)
    const nextCheck1 = computed(() => !firstTime.value);

    const handleNext = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleNext' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );

      if (nextCheck1.value) {
        store.commit(
          'addFormDebugMessage',
          componentIdentifier +
            ': nextCheck1 is TRUE (firstTime.Certifications is false), moving ahead 2 steps to Insurance-1'
        );
        return formRouter.moveToStepByIdentifier('Insurance-1');
      } else {
        store.commit(
          'addFormDebugMessage',
          componentIdentifier +
            ': nextCheck1 is FALSE (firstTime.Insurance is true)'
        );
      }

      formRouter.moveToNextStep();
    };

    const handleBack = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleBack' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.moveToPreviousStep();
    };

    const handleEdit = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleEdit' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.editActiveStep();
    };

    const emitExitModal = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'emitExitModal' called"
      );
      emit('exit-modal');
    };

    return {
      componentIdentifier,
      handleNext,
      handleBack,
      handleEdit,
      buttonsToDisplay,
      isButtonDisabled,
      isNavigationDisabled,
      firstTime,
      emitExitModal,
    };
  },
  data() {
    return {
      fmsca_certifications: [],
      filteredCertifications: [
        'CARB Truck and Bus',
        'CTPAT',
        'Tank Endorsed Drivers',
        'TSA',
        'TWIC',
        'Women-Owned Business',
        'Minority-Owned Business',
        'Veteran-Owned Business',
        'Disability-Owned Business',
      ],
      filteredUnverified: ['PHMSA', 'SmartWay', 'CarbTru'],
      filteredUnverifiedObj: [
        { is_type: 'PHMSA' },
        { is_type: 'SmartWay' },
        { is_type: 'CarbTru' },
      ],
    };
  },
  computed: {
    isCTBPending() {
      let returnedCerts = [...this.data.certifications];
      let pending = returnedCerts.filter((el) => {
        if (el.is_type == 'CARB Truck and Bus') {
          if (el.is_verified == true) {
            return false;
          } else {
            return true;
          }
        }
      });
      if (pending?.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    uncertifiedString() {
      let uncertifiedArr = [];
      this.uncertified.forEach((el) =>
        uncertifiedArr.push(
          el.is_type == 'PHMSA' ? el.is_type + ' (Hazmat)' : el.is_type
        )
      );
      return uncertifiedArr.join(', ');
    },
    pendingCertificationString() {
      let pendingArr = [];
      this.pendingCertifications.forEach((el) => pendingArr.push(el.is_type));
      return pendingArr.join(', ');
    },
    certified() {
      let certificationsCertified = [];
      let certs = this.data.certifications.map((c) => c.is_type);
      let self = this;
      if (this.firstTime) {
        certificationsCertified = this.filteredUnverifiedObj.filter((el) => {
          if (el.is_type == 'CARB Truck and Bus') {
            if (self.isCTBPending == false) {
              return certs.includes(el.is_type);
            } else {
              return false;
            }
          } else {
            return certs.includes(el.is_type);
          }
        });
      } else {
        certificationsCertified = this.data.certifications.filter((el) => {
          if (el.is_type == 'CARB Truck and Bus') {
            if (self.isCTBPending == false) {
              return certs.includes(el.is_type);
            } else {
              return false;
            }
          } else {
            return stepInformation.certifications.fmsca_certifications_str.includes(
              el.is_type
            );
          }
        });
      }
      return certificationsCertified.map((certification) => {
        // if(certification.is_type === "Tank Endorsed Drivers") {
        //   certification.is_type = "Tanker Endorsed Drivers";
        // }
        return certification;
      });
    },
    uncertified() {
      let uncertifiedCertifications = [];
      let self = this;
      if (this.firstTime) {
        uncertifiedCertifications = this.filteredUnverifiedObj.filter((el) => {
          return !self.certified.some((verified) => {
            return el.is_type === verified.is_type;
          });
        });
      } else {
        uncertifiedCertifications =
          stepInformation.certifications.fmsca_certifications.filter((el) => {
            if (
              el.is_type == 'CARB Truck and Bus' &&
              self.isCTBPending == true
            ) {
              return false;
            } else {
              return !self.certified.some((certificate) => {
                return el.is_type === certificate.is_type;
              });
            }
          });
      }
      return uncertifiedCertifications;
    },
    pendingCertifications() {
      let pendingCertifications = [];
      if (!this.firstTime) {
        let certs = this.data.certifications.map((c) => c.is_type);
        let returnedCerts = [...this.data.certifications];
        pendingCertifications = returnedCerts.filter((el) => {
          if (el.is_type == 'CARB Truck and Bus') {
            if (el.is_verified == false) {
              return certs.includes(el.is_type);
            }
          } else {
            return false;
          }
        });
      }
      return pendingCertifications;
    },
  },
  mounted() {
    this.fmsca_certifications =
      stepInformation.certifications.fmsca_certifications;
  },
};
</script>

<style scoped>
label {
  font-size: 9px;
  line-height: 13.5px;
  @apply mb-1 font-bold;
}

.cert-id {
  font-size: 8px;
}
</style>
